import React, { useContext, useState, useEffect } from 'react';
import { MdCheckBox, MdCheckBoxOutlineBlank } from 'react-icons/md';
import { BASE_URL_API } from '../../References/urls';
import AuthContext from '../../Store/auth-context';
import styles from './BoosterPDSConfiguration.module.css';

const BoosterPDSConfiguration = ({campaignIdentifier}) => {
    const [isFetchingData, setIsFetchingData] = useState(false)
    const authCtx = useContext(AuthContext)
    const [form, setForm] = useState({
        campaignName: '',
        budget: '',
        expectedUsers: '',
        campaignStartDate: '',
        campaignEndDate: '',
        bigWinnersCount: '',
        bigWinnerMin: '',
        bigWinnerMax: '',
        bigWinnersRandom: true,
        maxScansPerUser: '',
        maxRewardPerUser: '',
        scanRewardCap: true,
        strictBudgetEnforcement: true,
        enableStreakBonus: false,
        enableJackpotScan: false,
        bonusEveryNthScan: '',
        spinToWinEnabled: false,
        testMode: false,
        loggingEnabled: true,
        scanRewardTiers: [{ scan: 1, min: '', max: '' }],
        bigWinAtScan: '', // <-- NEW
    });

    const updateForm = (key, value) => setForm({ ...form, [key]: value });

    const updateTier = (index, key, value) => {
        const updatedTiers = [...form.scanRewardTiers];
        updatedTiers[index][key] = value;
        setForm({ ...form, scanRewardTiers: updatedTiers });
    };



    const addTier = () => {
        const maxTiers = numeric(form.maxScansPerUser);
        if (form.scanRewardTiers.length < maxTiers) {
            setForm({
                ...form,
                scanRewardTiers: [...form.scanRewardTiers, { scan: form.scanRewardTiers.length + 1, min: '', max: '' }]
            });
        } else {
            alert(`Max scan tiers allowed is ${maxTiers}. You've already added ${form.scanRewardTiers.length}.`);
        }
    };


    const removeTier = (indexToRemove) => {
        const updatedTiers = form.scanRewardTiers
            .filter((_, idx) => idx !== indexToRemove)
            .map((tier, idx) => ({
                ...tier,
                scan: idx + 1, // reassign sequential scan numbers
            }));

        setForm({ ...form, scanRewardTiers: updatedTiers });
    };


    const handleSubmit = async (e) => {
        console.log(form)
        e.preventDefault();
        try {
            const response = await fetch(BASE_URL_API+'/updateBoosterPdsConfig', {
                method: 'POST',
                body: JSON.stringify({
                    token : authCtx.token,
                    campaignIdentifier,
                    form
                })
            });
            const result = await response.json();
            if(result?.message==="token Expired, please login again!") {
                authCtx.logout()
            } else {

            }
            console.log(result);
            alert('Configuration saved successfully!');
        } catch (err) {
            console.error('Error saving configuration:', err);
            alert('Failed to save configuration.');
        }
    };

    const getBoosterPdsConfig = async () => {
        setIsFetchingData(true)
        const response = await fetch(BASE_URL_API+"/getBoosterPdsConfig",
        {
            method : "POST",
            body : JSON.stringify({
                token : authCtx.token,
                campaignIdentifier : campaignIdentifier
            })
        });

        if(!response.ok) {
            console.log("Something went wrong : Server Error")
        } else {
            const respo = await response.json()
            if(respo.status==="success") {
                const data = respo?.response;
                // ✅ Fix: Parse scanRewardTiers if it's a string
                if (typeof data.scanRewardTiers === "string") {
                    try {
                        data.scanRewardTiers = JSON.parse(data.scanRewardTiers);
                    } catch (err) {
                        console.error("Failed to parse scanRewardTiers", err);
                        data.scanRewardTiers = [];
                    }
                }

                setForm(data);
                console.log(data);
            } else {
                if(respo?.message==="token Expired, please login again!") {
                    authCtx.logout()
                }
            }
        }
        setIsFetchingData(false)
    }

    useEffect(() => {
        getBoosterPdsConfig()
    }, [campaignIdentifier])
    


    const numeric = (val) => Math.max(0, Number(val) || 0);

    const getTotalPoints = (tiers, key) => {
        return tiers.reduce((acc, tier) => acc + numeric(tier[key]), 0);
    };

    const calculateEstimatedRewards = () => {
        const users = numeric(form.expectedUsers);
        const scansPerUser = numeric(form.maxScansPerUser);

        const minTotal = users * getTotalPoints(form.scanRewardTiers, 'min');
        const maxTotal = users * getTotalPoints(form.scanRewardTiers, 'max');

        const averageTierReward = form.scanRewardTiers.reduce((acc, tier) => {
            const min = numeric(tier.min);
            const max = numeric(tier.max);
            if (min && max) {
                acc += (min + max) / 2;
            }
            return acc;
        }, 0) / (form.scanRewardTiers.length || 1);

        const averageTotal = Math.ceil(users * scansPerUser * averageTierReward);

        return {
            minTotal,
            maxTotal,
            averageTotal,
            remainingMin: numeric(form.budget) - minTotal,
            remainingMax: numeric(form.budget) - maxTotal,
        };
    };

    const {
        minTotal,
        maxTotal,
        averageTotal,
        remainingMin,
        remainingMax
    } = calculateEstimatedRewards();

    const generateSampleUsers = () => {
        const totalBudget = numeric(form.budget);
        const expectedUsers = numeric(form.expectedUsers);
        const maxScans = numeric(form.maxScansPerUser);
        const bigWinnersCount = numeric(form.bigWinnersCount);
        const bigWinnerMin = numeric(form.bigWinnerMin);
        const bigWinnerMax = numeric(form.bigWinnerMax);
        const bigWinAtScan = numeric(form.bigWinAtScan);
    
        const scanTiers = form.scanRewardTiers;
        let remainingBudget = totalBudget;
        const users = [];
    
        // Select random unique indexes for big winners
        const bigWinnerIndexes = new Set();
        while (bigWinnerIndexes.size < bigWinnersCount && bigWinnerIndexes.size < expectedUsers) {
            bigWinnerIndexes.add(Math.floor(Math.random() * expectedUsers));
        }
    
        for (let userIndex = 0; userIndex < expectedUsers; userIndex++) {
            const userScans = [];
            let userTotal = 0;
            let actualBigWinner = false;
    
            const isBigWinnerPlanned = bigWinnerIndexes.has(userIndex);
            const bigWinnerScan = bigWinAtScan > 1
                ? bigWinAtScan
                : 2 + Math.floor(Math.random() * (maxScans - 1));
    
            let campaignEnded = false;
    
            for (let scanIndex = 0; scanIndex < maxScans; scanIndex++) {
                const scanNo = scanIndex + 1;
                const tier = scanTiers[scanIndex] || {};
                let reward = 0;
    
                if (remainingBudget <= 0) {
                    campaignEnded = true;
                    break;
                }
    
                if (isBigWinnerPlanned && scanNo === bigWinnerScan && remainingBudget >= bigWinnerMin) {
                    reward = Math.floor(Math.random() * (bigWinnerMax - bigWinnerMin + 1)) + bigWinnerMin;
    
                    if (form.strictBudgetEnforcement && remainingBudget - reward < 0) {
                        reward = Math.floor(remainingBudget);
                    }
    
                    actualBigWinner = true;
                } else {
                    const min = Math.floor(numeric(tier.min));
                    const max = Math.floor(numeric(tier.max));
    
                    if (min && max && max > min) {
                        reward = Math.floor(Math.random() * (max - min + 1)) + min;
    
                        if (form.strictBudgetEnforcement && remainingBudget - reward < 0) {
                            reward = Math.floor(remainingBudget);
                        }
                    }
                }
    
                remainingBudget -= reward;
                userTotal += reward;
                userScans.push({ scanNo, reward });
    
                if (form.strictBudgetEnforcement && remainingBudget <= 0) break;
            }
    
            if (campaignEnded || remainingBudget <= 0) {
                users.push({
                    name: `User ${userIndex + 1}`,
                    scans: [],
                    total: 0,
                    message: 'Campaign ended - winner announced'
                });
            } else {
                users.push({
                    name: `User ${userIndex + 1}`,
                    scans: userScans,
                    total: userTotal,
                    bigWinner: actualBigWinner ? `Yes (Scan ${bigWinnerScan})` : 'No'
                });
            }
        }
    
        return users;
    };
    
    
    


    const sampleUsers = generateSampleUsers();




    return (
        <div className={styles.wrapper}>
            <div className={styles.heading}>Booster PDS Configuration {isFetchingData===true && `Please wait...`} </div>
            <div className={styles.mainContainer}>
                <div className={styles.configSettings}>
                    <form onSubmit={handleSubmit} className={styles.form}>
                        <div className={styles.eachInputGroup}>
                            <label className={styles.label}>Campaign Name
                                <input type="text" className={styles.input} value={form.campaignName} onChange={e => updateForm('campaignName', e.target.value)} />
                            </label>

                            <label className={styles.label}>Start Date
                                <input type="date" className={styles.input} value={form.campaignStartDate} onChange={e => updateForm('campaignStartDate', e.target.value)} />
                            </label>

                            <label className={styles.label}>End Date
                                <input type="date" className={styles.input} value={form.campaignEndDate} onChange={e => updateForm('campaignEndDate', e.target.value)} />
                            </label>
                        </div>
                        <div className={styles.eachInputGroup}>
                            <label className={styles.label}>Total Budget
                                <input type="number" className={styles.input} value={form.budget} onChange={e => updateForm('budget', e.target.value)} />
                            </label>

                            <label className={styles.label}>Expected Users
                                <input type="number" className={styles.input} value={form.expectedUsers} onChange={e => updateForm('expectedUsers', e.target.value)} />
                            </label>


                            <label className={styles.label}>Big Winners Count
                                <input type="number" className={styles.input} value={form.bigWinnersCount} onChange={e => updateForm('bigWinnersCount', e.target.value)} />
                            </label>
                        </div>

                        <div className={styles.eachInputGroup}>
                            <label className={styles.label}>Big Winner Reward Range (Min)
                                <input type="number" className={styles.input} value={form.bigWinnerMin} onChange={e => updateForm('bigWinnerMin', e.target.value)} />
                            </label>

                            <label className={styles.label}>Big Winner Reward Range (Max)
                                <input type="number" className={styles.input} value={form.bigWinnerMax} onChange={e => updateForm('bigWinnerMax', e.target.value)} />
                            </label>

                            <label className={styles.label}>Big Win At Scan (Optional)
                                <input
                                    type="number"
                                    className={styles.input}
                                    value={form.bigWinAtScan}
                                    onChange={e => updateForm('bigWinAtScan', e.target.value)}
                                />
                            </label>


                        </div>

                        <div className={styles.eachInputGroup}>
                            <label className={styles.label}>Max Scans Per User
                                <input type="number" className={styles.input} value={form.maxScansPerUser} onChange={e => updateForm('maxScansPerUser', e.target.value)} />
                            </label>

                            <label className={styles.label}>Max Reward Per User
                                <input type="number" className={styles.input} value={form.maxRewardPerUser} onChange={e => updateForm('maxRewardPerUser', e.target.value)} />
                            </label>
                        </div>
                        <h3 className={styles.subheading}>Scan Reward Tiers</h3>
                        {form.scanRewardTiers.map((tier, index) => (
                            <div key={index} className={styles.tierRow}>
                                <label className={styles.tierLabel}>Scan #{tier.scan}</label>
                                <input type="number" className={styles.tierInput} autoFocus placeholder="Min" value={tier.min} onChange={e => updateTier(index, 'min', e.target.value)} />
                                <input type="number" className={styles.tierInput} placeholder="Max" value={tier.max} onChange={e => updateTier(index, 'max', e.target.value)} />
                                {index > 0 && (
                                    <button type="button" className={styles.removeTierBtn} onClick={() => removeTier(index)}>Remove</button>
                                )}
                            </div>
                        ))}
                        <button type="button" className={styles.addTierBtn} onClick={addTier}>Add Tier</button>


                        <label className={styles.checkboxLabel}>Cap Scan Rewards When Near Budget
                            <input type="checkbox" className={styles.checkbox} checked={form.scanRewardCap} onChange={e => updateForm('scanRewardCap', e.target.checked)} />
                        </label>

                        <label className={styles.checkboxLabel}>Strict Budget Enforcement
                            <input type="checkbox" className={styles.checkbox} checked={form.strictBudgetEnforcement} onChange={e => updateForm('strictBudgetEnforcement', e.target.checked)} />
                        </label>

                        <label className={styles.checkboxLabel}>Enable Streak Bonus
                            <input type="checkbox" className={styles.checkbox} checked={form.enableStreakBonus} onChange={e => updateForm('enableStreakBonus', e.target.checked)} />
                        </label>

                        <label className={styles.checkboxLabel}>Enable Jackpot Scan
                            <input type="checkbox" className={styles.checkbox} checked={form.enableJackpotScan} onChange={e => updateForm('enableJackpotScan', e.target.checked)} />
                        </label>

                        {/* <label className={styles.label}>Bonus Every Nth Scan
                            <input type="number" className={styles.input} value={form.bonusEveryNthScan} onChange={e => updateForm('bonusEveryNthScan', e.target.value)} />
                        </label> */}

                        <label className={styles.checkboxLabel}>Spin to Win Enabled
                            <input type="checkbox" className={styles.checkbox} checked={form.spinToWinEnabled} onChange={e => updateForm('spinToWinEnabled', e.target.checked)} />
                        </label>

                        {/* <label className={styles.checkboxLabel}>Test Mode
                            <input type="checkbox" className={styles.checkbox} checked={form.testMode} onChange={e => updateForm('testMode', e.target.checked)} />
                        </label> */}

                        <label className={styles.checkboxLabel}>Logging Enabled
                            <input type="checkbox" className={styles.checkbox} checked={form.loggingEnabled} onChange={e => updateForm('loggingEnabled', e.target.checked)} />
                        </label>

                        <button type="submit" className={styles.submitBtn}>Save Configuration</button>
                    </form>
                </div>
                <div className={styles.budgetSummary}>
                    <h3>Budget Summary Preview</h3>
                    <div>Total Budget: ₹{form.budget || 0}</div>
                    <div>Min Estimated Reward Distribution: ₹{minTotal}</div>
                    <div>Max Estimated Reward Distribution: ₹{maxTotal}</div>
                    <div>Avg Estimated Distribution: ₹{averageTotal}</div>
                    <div style={{ color: remainingMin < 0 || remainingMax < 0 ? 'red' : 'green' }}>
                        Budget Remaining (Min-Max): ₹{remainingMin} to ₹{remainingMax}
                    </div>
                    {(remainingMin < 0 || remainingMax < 0) && (
                        <div className={styles.warningText}>
                            ⚠️ Budget might be exceeded based on reward range. Please review reward tiers or budget.
                        </div>
                    )}


                    <div>

                        <h3>Sample Users Reward Preview</h3>
                        {sampleUsers.map((user, idx) => (
                            <div key={idx} className={styles.userPreview}>
                                <strong>
                                    {user.name}
                                    {user.bigWinner && user.bigWinner.startsWith('Yes') && (
                                        <span className={styles.bigWinnerBadge}> 🎉 Big Winner ({user.bigWinner})</span>
                                    )}
                                </strong>

                                {user.message ? (
                                    <div className={styles.endedMessage}>{user.message}</div>
                                ) : (
                                    <>
                                        <div className={styles.scanRow}>
                                            {user.scans.map((scan, index) => (
                                                <span key={index} className={styles.scanBox}>
                                                    Scan {scan.scanNo}: ₹{scan.reward}
                                                </span>
                                            ))}
                                        </div>
                                        <div>
                                            <em>Total: ₹{user.total}</em>
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}


                    </div>
                </div>

            </div>

        </div>
    );
};

export default BoosterPDSConfiguration;
